import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { KpiCard } from "@/components/KpiCard";
import { Card } from "@/components/ui/card";
import { formatEUR } from "@/lib/constants";
import {
  FileText, Send, Eye, MessageSquare, AlertCircle, PhoneCall, Trophy, XCircle, TrendingUp, Wallet, LifeBuoy, Sparkles, RefreshCw, Sun, Thermometer, Zap, Flame, Award
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";

export default function Reporting() {
  const [r, setR] = useState(null);

  useEffect(() => { (async () => { const x = await api.get("/stats/reporting"); setR(x.data); })(); }, []);

  if (!r) return <div className="text-slate-500">Wird geladen …</div>;

  const objChart = Object.entries(r.objection_breakdown || {}).map(([name, count]) => ({ name, count }));

  return (
    <div className="space-y-8" data-testid="reporting-page">
      <header>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Reporting</h1>
        <p className="mt-1 text-slate-600">Vertriebs-Kennzahlen Ihrer PV-Pipeline</p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KpiCard testid="rep-created" label="Erstellte Angebote" value={r.created} icon={FileText} accent="slate" />
        <KpiCard testid="rep-sent" label="Versendete Angebote" value={r.sent} icon={Send} accent="blue" />
        <KpiCard testid="rep-opened" label="Geöffnete Angebote" value={r.opened} icon={Eye} accent="amber" />
        <KpiCard testid="rep-responses" label="Antworten" value={r.responses} icon={MessageSquare} accent="emerald" />
        <KpiCard testid="rep-objections" label="Erkannte Einwände" value={r.objections} icon={AlertCircle} accent="orange" />
        <KpiCard testid="rep-callbacks" label="Rückruftermine" value={r.callbacks} icon={PhoneCall} accent="amber" />
        <KpiCard testid="rep-won" label="Gewonnen" value={r.won} icon={Trophy} accent="emerald" />
        <KpiCard testid="rep-lost" label="Verloren" value={r.lost} icon={XCircle} accent="red" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <KpiCard testid="rep-won-value" label="Gewonnener Umsatz" value={formatEUR(r.won_value)} icon={Wallet} accent="emerald" />
        <KpiCard testid="rep-avg-value" label="Ø Angebotswert" value={formatEUR(r.avg_value)} icon={TrendingUp} accent="slate" />
        <KpiCard testid="rep-rates" label="Antwort / Abschluss" value={`${r.response_rate}% / ${r.win_rate}%`} icon={Trophy} accent="orange" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="rep-recovery-section">
        <KpiCard testid="rep-recovered-revenue" label="Geretteter Umsatz" value={formatEUR(r.recovered_revenue || 0)} icon={LifeBuoy} accent="emerald" sublabel="Gewonnen nach Reaktivierung" />
        <KpiCard testid="rep-reactivated-value" label="Reaktivierter Angebotswert" value={formatEUR(r.reactivated_offer_value || 0)} icon={Sparkles} accent="orange" sublabel="Pipeline, die wieder warm wurde" />
        <KpiCard testid="rep-recovered-deals" label="Gerettete Deals" value={r.recovered_deals || 0} icon={RefreshCw} accent="emerald" />
        <KpiCard testid="rep-avg-recovered" label="Ø geretteter Angebotswert" value={formatEUR(r.avg_recovered_value || 0)} icon={TrendingUp} accent="slate" />
      </div>

      {/* Energie-Erweiterung Phase E — Produktpfade & Estimates */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="rep-energy-section">
        <KpiCard testid="rep-leads-pv" label="Leads · Solaranlage" value={r.leads_by_solution?.pv || 0} icon={Sun} accent="amber" />
        <KpiCard testid="rep-leads-hp" label="Leads · Wärmepumpe" value={r.leads_by_solution?.heat_pump || 0} icon={Thermometer} accent="blue" />
        <KpiCard testid="rep-leads-combo" label="Leads · Solar + WP" value={r.leads_by_solution?.pv_heat_pump || 0} icon={Zap} accent="orange" />
        <KpiCard testid="rep-hot-leads" label="Hot / Sofort-Leads" value={r.hot_leads_total || 0} icon={Flame} accent="orange" sublabel="aktiv und priorisiert" />
        <KpiCard testid="rep-estimates-created" label="Preisschätzungen erstellt" value={r.estimates_created || 0} icon={FileText} accent="slate" />
        <KpiCard testid="rep-estimates-sent" label="Preisschätzungen versendet" value={r.estimates_sent || 0} icon={Send} accent="emerald" />
      </div>

      <Card className="border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Einwände nach Typ</h2>
        {objChart.length === 0 ? (
          <p className="text-sm text-slate-500">Noch keine Einwände erfasst.</p>
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={objChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" stroke="#64748B" fontSize={12} />
                <YAxis stroke="#64748B" fontSize={12} />
                <Tooltip />
                <Bar dataKey="count" fill="#EA580C" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>
    </div>
  );
}
